import type { User } from "@core/account/types";
import { AUTH_TOKEN_KEY } from "@core/account/utils";
import { safeSessionStorage } from "@core/shared/utils/session-storage";
import { userAtom } from "@framework/config/atoms/user-atom";
import { useAction } from "@framework/shared/hooks";
import cache from "@mongez/cache";
import { useEffect, useRef } from "react";
import {
  getSafeLoginErrorDiagnostic,
  logLoginDiagnostic,
} from "../login-diagnostics";

const recoveryAttemptKey = "auth_recovery_attempted";

function getStoredUser(): User | null {
  const storedUser = cache.get(AUTH_TOKEN_KEY) as unknown;

  if (!storedUser || typeof storedUser !== "object") return null;

  const candidate = storedUser as Partial<User>;

  return typeof candidate.accessToken === "string" && candidate.accessToken
    ? (candidate as User)
    : null;
}

/**
 * Hook to handle mobile authentication recovery using localStorage backup
 * This solves the issue where mobile browsers drop cookies after inactivity
 */
export function useAuthRecovery(): void {
  const refreshCookiesAction = useAction<{ user: User }>(
    "/auth/refresh-cookies",
  );
  const refreshCookiesActionRef = useRef(refreshCookiesAction);

  refreshCookiesActionRef.current = refreshCookiesAction;

  useEffect(() => {
    const checkAndRecoverAuth = async (): Promise<void> => {
      try {
        const currentUser = userAtom.value;

        if (currentUser?.userType === "user" && currentUser.accessToken) {
          safeSessionStorage.removeItem(recoveryAttemptKey);
          cache.set(AUTH_TOKEN_KEY, currentUser);

          return;
        }

        if (currentUser?.userType !== "guest") return;

        const storedUser = getStoredUser();

        if (!storedUser) {
          safeSessionStorage.removeItem(recoveryAttemptKey);

          return;
        }

        if (safeSessionStorage.getItem(recoveryAttemptKey)) {
          safeSessionStorage.removeItem(recoveryAttemptKey);
          cache.remove(AUTH_TOKEN_KEY);

          return;
        }

        const attemptRecorded = safeSessionStorage.setItem(
          recoveryAttemptKey,
          "true",
        );

        if (!attemptRecorded) {
          logLoginDiagnostic("auth_recovery_skipped_without_storage", {
            stage: "authRecovery",
          });

          return;
        }

        const recovery = await refreshCookiesActionRef.current({
          accessToken: storedUser.accessToken,
        });

        if (!recovery.isOK || !recovery.data?.user?.accessToken) {
          cache.remove(AUTH_TOKEN_KEY);
          safeSessionStorage.removeItem(recoveryAttemptKey);

          return;
        }

        cache.set(AUTH_TOKEN_KEY, recovery.data.user);
        window.location.reload();
      } catch (error) {
        logLoginDiagnostic(
          "auth_recovery_failed",
          {
            stage: "authRecovery",
            ...getSafeLoginErrorDiagnostic(error),
          },
          "error",
        );

        cache.remove(AUTH_TOKEN_KEY);
        safeSessionStorage.removeItem(recoveryAttemptKey);
      }
    };

    const timeoutId = setTimeout(checkAndRecoverAuth, 0);

    return () => clearTimeout(timeoutId);
  }, []);
}
